import { CheckCircle, XCircle, Clock, ArrowRight } from 'lucide-react';
import { cn, Badge } from '@centraoneplus/ui';

interface PartialResult {
  actionType: string;
  actionIndex: number;
  success: boolean;
  duration: number;
}

interface ExecutionActionResultProps {
  result: PartialResult;
  actionLabels?: Record<string, string>;
  className?: string;
}

/**
 * Shows individual action result (success/fail) with duration and type.
 */
export function ExecutionActionResult({
  result,
  actionLabels = {},
  className,
}: ExecutionActionResultProps) {
  const { actionType, actionIndex, success, duration } = result;
  const actionLabel = actionLabels[actionType] || actionType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-lg border',
        success
          ? 'bg-green-50 border-green-200 dark:bg-green-900/10 dark:border-green-800'
          : 'bg-red-50 border-red-200 dark:bg-red-900/10 dark:border-red-800',
        className
      )}
    >
      {/* Status icon */}
      {success ? (
        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400 shrink-0" />
      ) : (
        <XCircle className="w-4 h-4 text-red-600 dark:text-red-400 shrink-0" />
      )}

      {/* Action index */}
      <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">
        #{actionIndex + 1}
      </span>

      {/* Action type */}
      <span className="flex-1 text-sm font-medium text-gray-900 dark:text-white truncate">
        {actionLabel}
      </span>

      {/* Duration */}
      <div className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 shrink-0">
        <Clock className="w-3 h-3" />
        <span>{duration}ms</span>
      </div>

      {/* Status badge */}
      <Badge
        variant={success ? 'success' : 'error'}
        size="sm"
        className="shrink-0"
      >
        {success ? 'OK' : 'FAIL'}
      </Badge>
    </div>
  );
}

interface ExecutionActionResultsListProps {
  results: PartialResult[];
  actionLabels?: Record<string, string>;
  className?: string;
}

/**
 * Shows a list of action results with a visual flow indicator.
 */
export function ExecutionActionResultsList({
  results,
  actionLabels,
  className,
}: ExecutionActionResultsListProps) {
  if (!results || results.length === 0) {
    return (
      <p className="text-sm text-gray-500 dark:text-gray-400 italic">
        No actions were executed before the failure.
      </p>
    );
  }

  return (
    <div className={cn('space-y-2', className)}>
      {results.map((result, index) => (
        <div key={`${result.actionIndex}-${result.actionType}`} className="relative">
          <ExecutionActionResult result={result} actionLabels={actionLabels} />
          {/* Connector arrow between actions */}
          {index < results.length - 1 && (
            <div className="absolute left-5 -bottom-2 z-10">
              <ArrowRight className="w-3 h-3 text-gray-400 dark:text-gray-500 rotate-90" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default ExecutionActionResult;
